/**
 * PricingPage.jsx — LUMINA 澄光命盘产品定价
 * Phase 102: 产品架构落地
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../constants/config";

const GD   = "#D4AF37";
const GDD  = "#B8940A";
const BG   = "#FDFBF7";
const TXT  = "#1A202C";
const TXT2 = "#4A5568";
const TXT3 = "#718096";

const PLANS = [
  {
    id: "free",
    name: "免费",
    nameEn: "Free",
    price: "$0",
    period: "",
    desc: "初次体验，了解你的命盘",
    cta: "开始使用",
    ctaLink: "/",
    features: [
      "老黄历 · 解梦 · 观音灵签 · 起名",
      "八字紫微免费排盘",
      "Oracle 问事 1次/天",
      "无需注册即可使用工具",
    ],
    highlight: false,
  },
  {
    id: "oracle",
    name: "问事 Oracle",
    nameEn: "Oracle",
    price: "$9.99",
    period: "/次",
    desc: "一个问题，四引擎解答",
    cta: "立即提问",
    ctaLink: "/oracle",
    features: [
      "四引擎自动路由（六爻/八字/紫微/奇门）",
      "引擎事实 + 叙事解读",
      "来源声明 · ScoreBreakdown",
      "按需付费，无需订阅",
    ],
    highlight: false,
  },
  {
    id: "chart",
    name: "能量档案",
    nameEn: "Life Chart",
    price: "$39.99",
    period: "/次",
    desc: "看清你的能量底色",
    cta: "查看命盘",
    ctaLink: "/audit",
    features: [
      "八字四柱 · 日主强弱 · 喜用神",
      "大运流转 · 紫微十二宫",
      "十神性格 · 空亡 · 特殊格局",
      "PDF 下载 · 买一次管一生",
    ],
    highlight: false,
  },
  {
    id: "pro",
    name: "澄光 Pro",
    nameEn: "LUMINA Pro",
    price: "$49.99",
    period: "/月",
    desc: "前瞻你的能量地图",
    cta: "免费试用 7 天",
    ctaLink: "/dashboard",
    features: [
      "澄光日课 · 每日能量（完整叙事+7天预测）",
      "每周一：未来 7 天能量地图",
      "每月1日：月前顾 · 流月×喜用×关键日期",
      "每月15日：关键节点提醒",
      "每月25日：下月能量预览",
      "Oracle 问事 30次/月",
      "7 天免费试用",
    ],
    highlight: true,
  },
  {
    id: "synastry",
    name: "合盘",
    nameEn: "Synastry",
    price: "$79.99",
    period: "/次",
    desc: "两个人的命盘交叉分析",
    cta: "合盘分析",
    ctaLink: "/synastry",
    features: [
      "2人合盘 $79.99（情侣/夫妻/合伙）",
      "3-6人合盘 $99.99（家庭/团队）",
      "五行互补 · 冲突化解",
      "流年交叉分析 · 关系关键月提醒",
    ],
    subNote: "合盘 ≠ 澄光Pro家庭版。合盘是两人命盘的交叉分析，澄光Pro家庭版是3人各自的前瞻。",
    highlight: false,
  },
];

const FAQ = [
  { q: "免费版够用吗？", a: "免费版涵盖老黄历、解梦、灵签、起名四大工具，外加每天一次 Oracle 问事。适合初次体验。需要深度命盘分析或前瞻规划时，再考虑付费产品。" },
  { q: "问事和命盘有什么区别？", a: "问事是一次性的问题解答——「这个投资方向合适吗？」。命盘是基于你生辰八字的终身分析——「我这一生的天赋和功课是什么？」。问事看事，命盘看人。" },
  { q: "澄光Pro的「前瞻」具体是什么？", a: "不是泛泛的「本周运势」。是基于你的命盘，精确计算未来7天、1个月、1个季度的能量节点——哪几天适合行动、哪几天宜静不宜动、关键日期标注、冲合预警。" },
  { q: "合盘和澄光Pro家庭版有什么区别？", a: "澄光Pro家庭版是3个人各自独立的前瞻（各看各的能量地图）。合盘是两个人的命盘放在一起做交叉分析（五行互补/冲突/化解）。买了家庭版，做合盘仍需单独付费。" },
  { q: "可以随时取消订阅吗？", a: "可以。而且支持暂停订阅——每年最多暂停2次，每次最长2个月。暂停期间不收费、不推送。回来时所有数据都在。" },
];

export default function PricingPage({ lang = "zh", user }) {
  const nav = useNavigate();
  const zh = lang === "zh";
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");
  const [synastryPeople, setSynastryPeople] = useState(2);

  const handleStripeCheckout = async (plan) => {
    setCheckoutError("");
    if (!user || !user.token) {
      nav("/login?redirect=/pricing");
      return;
    }
    setLoadingPlan(plan.id);
    try {
      const endpoint = {
        oracle: "/checkout/oracle",
        chart: "/checkout/chart",
        pro: "/checkout/pro",
        synastry: "/checkout/synastry",
      }[plan.id];
      if (!endpoint) { nav(plan.ctaLink); return; }
      const body = plan.id === "synastry" ? JSON.stringify({ people_count: synastryPeople }) : undefined;
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${user.token}` },
        body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      setCheckoutError(e.message);
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleCTA = (plan) => {
    if (plan.id === "free") {
      nav(plan.ctaLink);
    } else {
      // Confirm before redirecting to Stripe
      const productNames = { oracle: zh ? "Oracle 问事 $9.99" : "Oracle Q $9.99", chart: zh ? "命盘报告 $39.99" : "Life Chart $39.99", pro: zh ? "澄光 Pro $49.99/月" : "LUMINA Pro $49.99/mo", synastry: zh ? `合盘分析 ${synastryPeople}人 $${synastryPeople === 2 ? "79.99" : "99.99"}` : `Synastry ${synastryPeople}p $${synastryPeople === 2 ? "79.99" : "99.99"}` };
      const name = productNames[plan.id] || plan.name;
      const msg = zh ? `确认购买「${name}」？` : `Confirm purchase of "${name}"?`;
      if (window.confirm(msg)) {
        handleStripeCheckout(plan);
      }
    }
  };

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, ${BG} 0%, #F3EFE6 50%, ${BG} 100%)`,
      fontFamily: "\"Inter\",\"Noto Serif SC\",serif",
      color: TXT,
      paddingBottom: 80,
    }}>
      {/* Hero */}
      <div style={{
        textAlign: "center",
        padding: "80px 24px 48px",
      }}>
        <div style={{
          fontSize: 11,
          color: GD,
          letterSpacing: 4,
          textTransform: "uppercase",
          marginBottom: 16,
        }}>
          {zh ? "澄光命盘 · 产品定价" : "LUMINA · Pricing"}
        </div>
        <h1 style={{
          fontSize: zh ? 32 : 36,
          fontWeight: 600,
          margin: "0 0 12px",
          letterSpacing: 1,
          color: TXT,
        }}>
          {zh ? "引擎能量洞察" : "Engine-Powered Energy Insight"}
        </h1>
        <p style={{
          fontSize: 15,
          color: TXT2,
          maxWidth: 480,
          margin: "0 auto",
          lineHeight: 1.7,
        }}>
          {zh
            ? "四引擎精确计算 × 叙事解读。不是随口编——是引擎真算。"
            : "Four-engine calculation × narrative insight. Real computation, not fabrication."}
        </p>
      </div>

      {/* Plans Grid */}
      <div style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "0 24px",
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 16,
      }}>
        {PLANS.map((plan) => (
          <div key={plan.id} style={{
            background: plan.highlight
              ? `linear-gradient(160deg, #fff 0%, rgba(212,175,55,0.06) 100%)`
              : "rgba(255,255,255,0.70)",
            border: plan.highlight
              ? `1.5px solid ${GD}`
              : "1px solid rgba(0,0,0,0.06)",
            borderRadius: 16,
            padding: "28px 24px 24px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            boxShadow: plan.highlight
              ? "0 4px 24px rgba(212,175,55,0.12)"
              : "0 2px 8px rgba(0,0,0,0.03)",
          }}>
            {plan.highlight && (
              <div style={{
                position: "absolute",
                top: -10,
                left: "50%",
                transform: "translateX(-50%)",
                background: `linear-gradient(135deg, ${GD}, ${GDD})`,
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 2,
                padding: "4px 16px",
                borderRadius: 20,
              }}>
                {zh ? "推荐" : "RECOMMENDED"}
              </div>
            )}

            {/* Name */}
            <div style={{
              fontSize: 12,
              color: TXT3,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginBottom: 4,
            }}>
              {zh ? plan.nameEn : plan.name}
            </div>
            <div style={{
              fontSize: 20,
              fontWeight: 600,
              color: TXT,
              marginBottom: 8,
            }}>
              {plan.name}
            </div>

            {/* Price */}
            <div style={{ marginBottom: 12 }}>
              <span style={{
                fontSize: 36,
                fontWeight: 700,
                color: plan.highlight ? GD : TXT,
                letterSpacing: -1,
              }}>
                {plan.price}
              </span>
              <span style={{
                fontSize: 13,
                color: TXT3,
                marginLeft: 2,
              }}>
                {plan.period}
              </span>
            </div>

            {/* Description */}
            <p style={{
              fontSize: 13,
              color: TXT2,
              margin: "0 0 20px",
              lineHeight: 1.5,
            }}>
              {plan.desc}
            </p>

            {/* Features */}
            <ul style={{
              listStyle: "none",
              padding: 0,
              margin: "0 0 24px",
              flex: 1,
            }}>
              {plan.features.map((f, i) => (
                <li key={i} style={{
                  fontSize: 12.5,
                  color: TXT2,
                  padding: "6px 0",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  lineHeight: 1.5,
                  borderBottom: "1px solid rgba(0,0,0,0.03)",
                }}>
                  <span style={{ color: GD, flexShrink: 0 }}>✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {plan.subNote && (
              <div style={{
                fontSize: 11,
                color: TXT3,
                marginBottom: 16,
                padding: "8px 12px",
                background: "rgba(0,0,0,0.02)",
                borderRadius: 8,
                lineHeight: 1.5,
              }}>
                {plan.subNote}
              </div>
            )}

            {/* CTA */}
            {/* Synastry people count selector */}
            {plan.id === "synastry" && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: TXT3, marginBottom: 6, letterSpacing: 1 }}>
                  {zh ? "合盘人数" : "People"}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {[2, 3, 4, 5, 6].map(n => (
                    <button
                      key={n}
                      onClick={() => setSynastryPeople(n)}
                      style={{
                        flex: 1, padding: "6px 0", borderRadius: 8,
                        border: synastryPeople === n ? `1.5px solid ${GD}` : "1px solid rgba(0,0,0,0.1)",
                        background: synastryPeople === n ? `rgba(212,175,55,0.08)` : "transparent",
                        color: synastryPeople === n ? GD : TXT3,
                        fontSize: 13, fontWeight: synastryPeople === n ? 600 : 400,
                        cursor: "pointer",
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: TXT3, marginTop: 4, textAlign: "center" }}>
                  {synastryPeople === 2 ? "$79.99" : "$99.99"} CAD
                </div>
              </div>
            )}
            <button
              onClick={() => handleCTA(plan)}
              disabled={loadingPlan === plan.id}
              style={{
                width: "100%",
                padding: "12px 0",
                borderRadius: 10,
                border: plan.highlight ? "none" : `1px solid ${GD}`,
                background: plan.highlight
                  ? `linear-gradient(135deg, ${GD}, ${GDD})`
                  : "transparent",
                color: plan.highlight ? "#fff" : GD,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: 1,
                cursor: loadingPlan === plan.id ? "wait" : "pointer",
                opacity: loadingPlan === plan.id ? 0.7 : 1,
                transition: "all 0.2s",
              }}
            >
              {loadingPlan === plan.id ? (zh ? "跳转中…" : "Redirecting…") : plan.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Checkout Error */}
      {checkoutError && (
        <div style={{
          maxWidth: 1100, margin: "16px auto 0", padding: "0 24px",
        }}>
          <div style={{
            padding: "12px 20px", borderRadius: 10,
            background: "rgba(185,28,28,0.06)", border: "1px solid rgba(185,28,28,0.15)",
            color: "#b91c1c", fontSize: 13, textAlign: "center",
          }}>
            {checkoutError}
          </div>
        </div>
      )}

      {/* FAQ */}
      <div style={{
        maxWidth: 640,
        margin: "64px auto 0",
        padding: "0 24px",
      }}>
        <h3 style={{
          textAlign: "center",
          fontSize: 18,
          fontWeight: 600,
          color: TXT,
          marginBottom: 32,
          letterSpacing: 1,
        }}>
          {zh ? "常见问题" : "FAQ"}
        </h3>
        {FAQ.map((item, i) => (
          <div key={i} style={{
            marginBottom: 20,
            padding: "16px 20px",
            background: "rgba(255,255,255,0.60)",
            borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.04)",
          }}>
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: TXT,
              marginBottom: 6,
            }}>
              {item.q}
            </div>
            <div style={{
              fontSize: 13,
              color: TXT2,
              lineHeight: 1.7,
            }}>
              {item.a}
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div style={{
        textAlign: "center",
        marginTop: 48,
        fontSize: 12,
        color: TXT3,
      }}>
        {zh
          ? "所有价格以加元(CAD)计价。7天免费试用需绑定支付方式，试用期内不收费。"
          : "All prices in CAD. 7-day free trial requires a payment method, no charge during trial."}
      </div>
    </div>
  );
}
